import React, { useState, useEffect, useCallback } from 'react';
import { Card, Select, Table, Tag, Spin, Row, Col, Statistic, Empty, Tooltip } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, FireOutlined, CalendarOutlined, LineChartOutlined, BarChartOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import AYSService from '../../services/AYSService';
import { message } from 'antd';
import QoCAYSDashboard from './QoCAYSDashboard';

const { Option } = Select;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEARS  = [2024, 2025, 2026, 2027];

function scoreColor(v) {
    if (v >= 3.5) return '#16a34a';
    if (v >= 3.0) return '#1890ff';
    if (v >= 2.5) return '#d97706';
    return '#dc2626';
}
function scoreBg(v) {
    if (v === null || v === undefined) return '#f3f4f6';
    if (v >= 3.5) return '#dcfce7';
    if (v >= 3.0) return '#dbeafe';
    if (v >= 2.5) return '#fef3c7';
    return '#fee2e2';
}

export default function QoCAYSAnalytics() {
    const [data,        setData]        = useState(null);
    const [heatmap,     setHeatmap]     = useState(null);
    const [trend,       setTrend]       = useState([]);
    const [compliance,  setCompliance]  = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [tab,         setTab]         = useState('dashboard');
    const [year,        setYear]        = useState(new Date().getFullYear());
    const [month,       setMonth]       = useState(null);
    const [selFacility, setSelFacility] = useState(null);

    useEffect(() => { loadAll(); }, [year, month]); // eslint-disable-line

    const loadAll = useCallback(() => {
        setLoading(true);
        const params = { year };
        if (month) params.month = month;
        Promise.all([
            AYSService.getAnalytics(params),
            AYSService.getQuestionHeatmap(params),
            AYSService.getFacilityTrend({ year, facility_id: selFacility }),
            AYSService.getSubmissionCompliance({ year }),
        ]).then(([aRes, hRes, tRes, cRes]) => {
            setData(aRes.data?.data || null);
            setHeatmap(hRes.data?.data || null);
            setTrend(tRes.data?.data || []);
            setCompliance(cRes.data?.data || []);
        }).catch(e => message.error(e.message))
        .finally(() => setLoading(false));
    }, [year, month, selFacility]); // eslint-disable-line

    const summary = data?.summary || {};
    const national_mean = parseFloat(summary.national_mean || 0);

    // ── Filters bar ───────────────────────────────────────────────────────────
    const filtersBar = (
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:16 }}>
            <Select value={year} onChange={setYear} style={{ width:100 }}>
                {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            <Select allowClear value={month} onChange={setMonth} placeholder="All Months" style={{ width:130 }}>
                {MONTHS.map((m,i) => <Option key={i+1} value={i+1}>{m}</Option>)}
            </Select>
        </div>
    );

    // ── Tab 1: Summary + Questions ────────────────────────────────────────────
    const facilityCols = [
        { title:'Facility', dataIndex:'facility_name', sorter:(a,b)=>a.facility_name.localeCompare(b.facility_name) },
        { title:'Region',   dataIndex:'region',        width:120 },
        { title:'Assessments', dataIndex:'total_assessments', width:100, sorter:(a,b)=>a.total_assessments-b.total_assessments },
        { title:'Mean Score', dataIndex:'avg_mean', width:130, sorter:(a,b)=>a.avg_mean-b.avg_mean, defaultSortOrder:'descend',
            render: v => {
                const m = parseFloat(v||0);
                return <span style={{ fontWeight:700, color:scoreColor(m) }}>
                    {m.toFixed(2)} <Tag color={m>=3.5?'green':'red'} style={{fontSize:10}}>{m>=3.5?'Satisfied':'Not Satisfied'}</Tag>
                </span>;
            }
        },
        { title:'Qs Satisfied', dataIndex:'avg_satisfied',     width:110, render: v => <span style={{color:'#16a34a',fontWeight:700}}>{Math.round(v||0)}</span> },
        { title:'Qs Not Satisfied', dataIndex:'avg_not_satisfied', width:130, render: v => <span style={{color:'#dc2626',fontWeight:700}}>{Math.round(v||0)}</span> },
    ];
    const questionCols = [
        { title:'#', dataIndex:'display_order', width:50 },
        { title:'Question', dataIndex:'question_text', ellipsis:true },
        { title:'Section', dataIndex:'section', width:120, render: v => <Tag>{v}</Tag> },
        { title:'KP', dataIndex:'is_key_pop', width:60, render: v => v ? <Tag color="purple">KP</Tag> : null },
        { title:'Responses', dataIndex:'response_count', width:90, sorter:(a,b)=>a.response_count-b.response_count },
        { title:'Mean', dataIndex:'national_mean', width:90, sorter:(a,b)=>a.national_mean-b.national_mean, defaultSortOrder:'ascend',
            render: v => <span style={{fontWeight:700, color:scoreColor(parseFloat(v||0))}}>{parseFloat(v||0).toFixed(2)}</span>
        },
        { title:'Distribution', dataIndex:'always_count', width:200, render:(_, r) => {
            const total = (r.always_count||0)+(r.mostly_count||0)+(r.sometimes_count||0)+(r.never_count||0);
            if (!total) return null;
            return (
                <div style={{display:'flex',height:16,borderRadius:4,overflow:'hidden',width:'100%'}}>
                    {[{v:r.always_count,c:'#16a34a',l:'Always'},{v:r.mostly_count,c:'#1890ff',l:'Mostly'},
                      {v:r.sometimes_count,c:'#d97706',l:'Sometimes'},{v:r.never_count,c:'#dc2626',l:'Never'}]
                    .map(({v,c,l}) => v>0 ? (
                        <Tooltip key={l} title={`${l}: ${v} (${Math.round(v/total*100)}%)`}>
                            <div style={{width:`${v/total*100}%`,background:c,minWidth:2}} />
                        </Tooltip>
                    ) : null)}
                </div>
            );
        }},
        { title:'Satisfied', dataIndex:'is_satisfied', width:90,
            render: v => v ? <CheckCircleFilled style={{color:'#16a34a',fontSize:16}}/> : <CloseCircleFilled style={{color:'#dc2626',fontSize:16}}/>
        },
    ];

    // ── Tab 2: Question Heatmap ───────────────────────────────────────────────
    const heatmapTab = () => {
        if (!heatmap) return <Empty />;
        const { questions, facilities } = heatmap;
        if (!facilities.length) return <Empty description="No data for selected period" />;

        return (
            <div>
                <div style={{ marginBottom:10, fontSize:12, color:'#888' }}>
                    Colour scale: <span style={{background:'#dcfce7',padding:'1px 8px',borderRadius:3,marginRight:4}}>≥3.5 Satisfied</span>
                    <span style={{background:'#dbeafe',padding:'1px 8px',borderRadius:3,marginRight:4}}>3.0–3.5</span>
                    <span style={{background:'#fef3c7',padding:'1px 8px',borderRadius:3,marginRight:4}}>2.5–3.0</span>
                    <span style={{background:'#fee2e2',padding:'1px 8px',borderRadius:3,marginRight:4}}>&lt;2.5</span>
                    <span style={{background:'#f3f4f6',padding:'1px 8px',borderRadius:3}}>No data</span>
                </div>
                <div style={{ overflowX:'auto' }}>
                    <table style={{ borderCollapse:'collapse', fontSize:11, whiteSpace:'nowrap' }}>
                        <thead>
                            <tr>
                                <th style={{ padding:'6px 10px', textAlign:'left', background:'#1e3a5f', color:'#fff', minWidth:180 }}>Facility</th>
                                {questions.map(q => (
                                    <th key={q.id} style={{ padding:'4px 6px', textAlign:'center', background:'#1e3a5f', color:'#fff', minWidth:42, maxWidth:42 }}>
                                        <Tooltip title={q.question_text}>
                                            <span style={{ cursor:'help' }}>Q{q.display_order}</span>
                                        </Tooltip>
                                    </th>
                                ))}
                                <th style={{ padding:'4px 8px', textAlign:'center', background:'#1e3a5f', color:'#fff', minWidth:70 }}>Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {facilities.map((f, i) => {
                                const scores = questions.map(q => f.scores[q.id] ?? null);
                                const valid = scores.filter(s => s !== null);
                                const avg = valid.length ? (valid.reduce((a,b)=>a+b,0)/valid.length) : null;
                                return (
                                    <tr key={f.facility_id} style={{ background: i%2===0 ? '#fff' : '#f8fafc' }}>
                                        <td style={{ padding:'5px 10px', fontWeight:600, fontSize:11 }}>{f.facility_name}</td>
                                        {scores.map((s, qi) => (
                                            <td key={qi} style={{ padding:'4px 6px', textAlign:'center', background:scoreBg(s) }}>
                                                {s !== null ? <span style={{ color:scoreColor(s), fontWeight:700 }}>{s.toFixed(1)}</span> : <span style={{color:'#ccc'}}>—</span>}
                                            </td>
                                        ))}
                                        <td style={{ padding:'4px 8px', textAlign:'center', background:scoreBg(avg), fontWeight:800 }}>
                                            {avg !== null ? <span style={{color:scoreColor(avg)}}>{avg.toFixed(2)}</span> : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ── Tab 3: Trend per Facility ─────────────────────────────────────────────
    const trendTab = () => {
        if (!trend.length) return <Empty description="No trend data for selected year" />;

        const option = {
            tooltip: { trigger:'axis' },
            legend: { data: trend.map(f => f.facility_name), type:'scroll', bottom:0 },
            xAxis: { type:'category', data: MONTHS },
            yAxis: { type:'value', min:1, max:4, axisLabel:{ formatter: v => v===4?'Always':v===3?'Mostly':v===2?'Sometimes':v===1?'Never':v } },
            series: trend.map(f => ({
                name: f.facility_name,
                type: 'line',
                smooth: true,
                data: Array.from({length:12}, (_, i) => f.months[i+1] ?? null),
                connectNulls: false,
            })),
            grid: { left:60, right:20, top:20, bottom:80 },
        };

        // Improvement rate table
        const improvementData = trend.map(f => {
            const pts = Object.entries(f.months).map(([m, s]) => ({ m: parseInt(m), s })).sort((a,b)=>a.m-b.m);
            if (pts.length < 2) return { ...f, trend: 'insufficient', change: null };
            const first = pts[0].s, last = pts[pts.length-1].s;
            const change = last - first;
            return { ...f, trend: change > 0.1 ? 'improving' : change < -0.1 ? 'declining' : 'stable', change: change.toFixed(2) };
        });

        return (
            <div>
                <Card size="small" title="Monthly Trend — All Enrolled Facilities" style={{ marginBottom:16 }}>
                    <ReactECharts option={option} style={{ height: Math.max(300, trend.length * 20 + 120) }} />
                </Card>
                <Card size="small" title="Improvement Rate">
                    <Table size="small" dataSource={improvementData} rowKey="facility_id" pagination={false}
                        columns={[
                            { title:'Facility', dataIndex:'facility_name' },
                            { title:'Trend', dataIndex:'trend', width:110, render: v => (
                                <Tag color={v==='improving'?'green':v==='declining'?'red':'blue'}>{v}</Tag>
                            )},
                            { title:'Score Change', dataIndex:'change', width:120, render: v => v === null ? '—' : (
                                <span style={{fontWeight:700, color: v>0?'#16a34a':v<0?'#dc2626':'#888'}}>
                                    {v>0?'+':''}{v}
                                </span>
                            )},
                            { title:'Months Submitted', render:(_, r) => Object.keys(r.months).length },
                        ]}
                    />
                </Card>
            </div>
        );
    };

    // ── Tab 4: Submission Compliance ──────────────────────────────────────────
    const complianceTab = () => {
        if (!compliance.length) return <Empty description="No enrolled facilities found" />;

        return (
            <div>
                <div style={{ marginBottom:10, fontSize:12, color:'#888' }}>
                    <span style={{background:'#dcfce7',padding:'1px 8px',borderRadius:3,marginRight:4}}>Submitted ≥3.5</span>
                    <span style={{background:'#dbeafe',padding:'1px 8px',borderRadius:3,marginRight:4}}>Submitted &lt;3.5</span>
                    <span style={{background:'#f3f4f6',padding:'1px 8px',borderRadius:3}}>Not submitted</span>
                </div>
                <div style={{ overflowX:'auto' }}>
                    <table style={{ borderCollapse:'collapse', fontSize:11, whiteSpace:'nowrap', width:'100%' }}>
                        <thead>
                            <tr>
                                <th style={{ padding:'6px 10px', textAlign:'left', background:'#1e3a5f', color:'#fff', minWidth:180 }}>Facility</th>
                                <th style={{ padding:'6px 8px', background:'#1e3a5f', color:'#fff', minWidth:90 }}>Region</th>
                                {MONTHS.map(m => (
                                    <th key={m} style={{ padding:'4px 8px', textAlign:'center', background:'#1e3a5f', color:'#fff', minWidth:46 }}>{m}</th>
                                ))}
                                <th style={{ padding:'4px 8px', textAlign:'center', background:'#1e3a5f', color:'#fff', minWidth:80 }}>Submitted</th>
                                <th style={{ padding:'4px 8px', textAlign:'center', background:'#1e3a5f', color:'#fff', minWidth:80 }}>Compliance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {compliance.map((f, i) => (
                                <tr key={f.facility_id} style={{ background: i%2===0 ? '#fff' : '#f8fafc' }}>
                                    <td style={{ padding:'5px 10px', fontWeight:600 }}>{f.facility_name}</td>
                                    <td style={{ padding:'5px 8px', color:'#888' }}>{f.region||'—'}</td>
                                    {Array.from({length:12}, (_, mi) => {
                                        const sub = f.months[mi+1];
                                        return (
                                            <td key={mi} style={{ padding:'4px 8px', textAlign:'center',
                                                background: sub ? (sub.mean>=3.5?'#dcfce7':'#dbeafe') : '#f3f4f6' }}>
                                                {sub ? (
                                                    <Tooltip title={`Mean: ${sub.mean}`}>
                                                        <CheckCircleFilled style={{ color: sub.mean>=3.5?'#16a34a':'#1890ff', fontSize:14 }} />
                                                    </Tooltip>
                                                ) : <span style={{color:'#ccc',fontSize:12}}>—</span>}
                                            </td>
                                        );
                                    })}
                                    <td style={{ padding:'4px 8px', textAlign:'center', fontWeight:700, color:'#1890ff' }}>
                                        {f.submitted_count}/12
                                    </td>
                                    <td style={{ padding:'4px 8px', textAlign:'center' }}>
                                        <Tag color={f.compliance_pct>=75?'green':f.compliance_pct>=50?'orange':'red'}>
                                            {f.compliance_pct}%
                                        </Tag>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>AYS Analytics</h4>
                    <small className="text-muted">Adolescent & Youth Services — Scorecard Analytics</small>
                </div>
            </div>
            <div className="page-pad">
                {tab !== 'dashboard' && filtersBar}
                <Spin spinning={loading}>
                    {/* Summary KPIs */}
                    {data && tab !== 'dashboard' && (
                        <Row gutter={12} style={{ marginBottom:16 }}>
                            {[
                                { label:'Total Assessments', value:summary.total_assessments||0, color:'#1890ff' },
                                { label:'Enrolled Facilities', value:summary.total_facilities||0, color:'#7c3aed' },
                                { label:'National Mean', value:national_mean.toFixed(2), color:scoreColor(national_mean) },
                                { label:'Satisfied Facilities', value:summary.satisfied_facilities||0, color:'#16a34a' },
                                { label:'Not Satisfied', value:summary.not_satisfied_facilities||0, color:'#dc2626' },
                            ].map(k => (
                                <Col key={k.label} xs={12} sm={8} md={4}>
                                    <Card size="small">
                                        <Statistic title={k.label} value={k.value}
                                            valueStyle={{ color:k.color, fontWeight:800, fontSize:22 }} />
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {/* Tab nav */}
                    <div style={{ display:'flex', gap:0, borderBottom:'2px solid #f0f0f0', marginBottom:16 }}>
                        {[
                            { key:'dashboard', label:'Dashboard', icon:<BarChartOutlined/> }, { key:'questions',  label:'Questions (National)',  icon:<BarChartOutlined/> },
                            { key:'heatmap',    label:'Question Heatmap',      icon:<FireOutlined/> },
                            { key:'trend',      label:'Facility Trends',       icon:<LineChartOutlined/> },
                            { key:'compliance', label:'Submission Compliance', icon:<CalendarOutlined/> },
                        ].map(t => (
                            <div key={t.key} onClick={() => setTab(t.key)}
                                style={{ padding:'8px 18px', cursor:'pointer', fontSize:13, fontWeight:600,
                                    color: tab===t.key ? '#1890ff' : '#555',
                                    borderBottom: tab===t.key ? '2px solid #1890ff' : '2px solid transparent',
                                    marginBottom:-2, display:'flex', alignItems:'center', gap:6 }}>
                                {t.icon} {t.label}
                            </div>
                        ))}
                    </div>
                    {/* Tab content */}
                    {tab === 'dashboard' && <QoCAYSDashboard />}
                    {tab === 'questions' && (
                        <div>
                            <Table size="small" dataSource={data?.by_question||[]} rowKey="id"
                                pagination={false} columns={questionCols} />
                            <div style={{ marginTop:20 }}>
                                <Table size="small" dataSource={data?.by_facility||[]} rowKey="facility_id"
                                    pagination={{ pageSize:20 }} columns={facilityCols} />
                            </div>
                        </div>
                    )}
                    {tab === 'heatmap'    && heatmapTab()}
                    {tab === 'trend'      && trendTab()}
                    {tab === 'compliance' && complianceTab()}
                </Spin>
            </div>
        </div>
    );
}
